import type {
  APIData,
  Question,
  Response,
  Artifact,
  ClauseInfo,
} from "./ReviewResponseModel.types";

export const transformAPIData = (apiData: APIData) => {
  const questions: Question[] = [];
  const responses: Response[] = [];

  apiData.questions.forEach((apiQuestion, index) => {
    const questionId = `q${apiQuestion.questionId}`;

    // Create question object
    questions.push({
      id: questionId,
      title: `${apiQuestion.themeName || "Question"}`,
      clauseId: `${apiQuestion.clauseId || "N/A"}`,
      questionText: apiQuestion.question || "No question text available",
      totalResponses: apiQuestion.responses?.length || 0,
      reviewedCount: 0,
    });

    // Create response objects
    if (apiQuestion.responses && apiQuestion.responses.length > 0) {
      apiQuestion.responses.forEach((apiResponse, respIndex) => {
        // Get the latest conversation (assuming first one is the latest)
        const conversation = apiResponse.conversations?.[0];

        if (conversation) {
          // Transform documents to artifacts
          const artifacts: Artifact[] = (conversation.documents || []).map(
            (doc: any, docIndex: number) => ({
              id: `artifact-${apiQuestion.questionId}-${respIndex}-${docIndex}`,
              fileName:
                doc.fileName || doc.name || `document-${docIndex + 1}.pdf`,
              fileSize: doc.fileSize || doc.size || "Unknown",
              uploadedAt:
                doc.uploadedAt || doc.createdAt || conversation.respondedOn,
            }),
          );

          // Determine AI evaluation status
          const aiStatus =
            conversation.aiValidation?.satisfactory === "yes"
              ? "Satisfied"
              : "Not Satisfied";
          const aiReason =
            conversation.aiValidation?.reason || "No evaluation provided";

          responses.push({
            id: `r${conversation.conversationId}`,
            questionId: questionId,
            clauseId: questionId,
            responderName: apiResponse.auditeeName || "Unknown",
            responseText: conversation.response || "No response provided",
            respondedAt: new Date(conversation.respondedOn).toLocaleString(
              "en-US",
              {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              },
            ),
            artifacts: artifacts,
            questionCode: conversation.questionCode,
            aiEvaluation: {
              status: aiStatus,
              reason: aiReason,
              timestamp: new Date(conversation.respondedOn).toLocaleString(
                "en-US",
                {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                },
              ),
            },
          });
        }
      });
    }
  });

  // Create clause info from first question
  const firstQuestion = apiData.questions?.[0];
  const clauseInfo: ClauseInfo = {
    clauseId: `${firstQuestion?.clauseId || "N/A"}`,
    description:
      firstQuestion?.purpose ||
      "This question requires a comprehensive assessment of how the organization has structured and categorized its critical business processes.",
    purpose:
      firstQuestion?.purpose ||
      "To establish transparency in business process management and ensure all critical functions are properly identified, documented, and protected according to their importance and dependencies.",
    artifacts: firstQuestion?.artifacts?.split(", ") || [],
  };

  return { questions, responses, clauseInfo };
};
